import { idb } from "../../db/index.ts";
import { g, local } from "../../util/index.ts";
import isUntradable from "./isUntradable.ts";
import makeItWork from "./makeItWork.ts";
import processTrade from "./processTrade.ts";
import summary from "./summary.ts";
import type { TradeTeams } from "../../../common/types.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { choice } from "../../../common/random.ts";
import { ValueChangeCalculator } from "../team/ValueChangeCalculator.ts";

const getAITids = async () => {
	const teams = await idb.cache.teams.getAll();
	return teams
		.filter((t) => {
			if (t.disabled) {
				return false;
			}

			// Multiplayer: the sim runner advances the shared league in spectator
			// mode (so the engine keeps AI rosters legal), but human teams are real
			// players' teams - the AI must NEVER trade their players. Without this,
			// the spectator branch below would treat every team as AI-tradeable.
			// Same guard as autoSign; see mpProtectUserTeams.
			if (local.mpProtectUserTeams && g.get("userTids").includes(t.tid)) {
				return false;
			}

			if (
				(local.autoPlayUntil || g.get("spectator")) &&
				!g.get("challengeNoTrades")
			) {
				return true;
			}
			return !g.get("userTids").includes(t.tid);
		})
		.map((t) => t.tid);
};

const attempt = async (valueChangeCalculator: ValueChangeCalculator) => {
	const aiTids = await getAITids();

	if (aiTids.length === 0) {
		return false;
	}

	const tid = choice(aiTids);
	const otherTids = aiTids.filter((tid2) => tid !== tid2);

	if (otherTids.length === 0) {
		return false;
	}

	const otherTid = choice(otherTids);
	const players = (
		await idb.cache.players.indexGetAll("playersByTid", tid)
	).filter((p) => !isUntradable(p).untradable);
	const draftPicks = await idb.cache.draftPicks.indexGetAll(
		"draftPicksByTid",
		tid,
	);

	if (players.length === 0 && draftPicks.length === 0) {
		return false;
	}

	const r = Math.random();
	const pids: number[] = [];
	const dpids: number[] = [];

	if ((r < 0.7 || draftPicks.length === 0) && players.length > 0) {
		// Weight by player value - good player more likely to be in trade
		const p = choice(players, (p) => p.value);
		if (!p) {
			return false;
		}
		pids.push(p.pid);
	} else if ((r < 0.85 || players.length === 0) && draftPicks.length > 0) {
		dpids.push(choice(draftPicks).dpid);
	} else {
		// Weight by player value - good player more likely to be in trade
		const p = choice(players, (p) => p.value);
		const dp = choice(draftPicks);
		if (!p || !dp) {
			return false;
		}
		pids.push(p.pid);
		dpids.push(dp.dpid);
	}

	const teams0: TradeTeams = [
		{
			dpids,
			dpidsExcluded: [],
			pids,
			pidsExcluded: [],
			tid,
		},
		{
			dpids: [],
			dpidsExcluded: [],
			pids: [],
			pidsExcluded: [],
			tid: otherTid,
		},
	];

	const teams = await makeItWork(teams0, {
		holdUserConstant: false,
		maxAssetsToAdd: 5,
		valueChangeCalculator,
	});

	if (!teams) {
		return false;
	}

	// Don't do trades of just picks, it's weird usually
	if (teams[0].pids.length === 0 && teams[1].pids.length === 0) {
		return false;
	}

	// Don't do trades for nothing, it's weird usually
	if (teams[1].pids.length === 0 && teams[1].dpids.length === 0) {
		return false;
	}

	const tradeSummary = await summary(teams);

	if (tradeSummary.warning) {
		return false;
	}

	// Make sure this isn't a really shitty trade
	const dv2 = await valueChangeCalculator.evaluate({
		tid: teams[0].tid,
		pidsAdd: teams[1].pids,
		pidsRemove: teams[0].pids,
		dpidsAdd: teams[1].dpids,
		dpidsRemove: teams[0].dpids,
		tradingPartnerTid: undefined,
	});
	if (Math.abs(dv2) > 15) {
		return false;
	}

	const finalTids: [number, number] = [teams[0].tid, teams[1].tid];
	const finalPids: [number[], number[]] = [teams[0].pids, teams[1].pids];
	const finalDpids: [number[], number[]] = [teams[0].dpids, teams[1].dpids];
	await processTrade(finalTids, finalPids, finalDpids);

	return finalTids;
};

const DEFAULT_NUM_TEAMS = 30;

const betweenAiTeams = async () => {
	if (g.get("forceHistoricalRosters")) {
		return false;
	}

	// If aiTradesFactor is not an integer, use the fractional part as a probability. Like for 3.5, 50% of the times it will be 3, and 50% will be 4.
	// Also scale so there are fewer trade attempts if there are fewer teams.
	let float = g.get("aiTradesFactor");
	if (isSport("baseball")) {
		float *= 0.25;
	}
	if (g.get("numActiveTeams") < DEFAULT_NUM_TEAMS) {
		float *= g.get("numActiveTeams") / DEFAULT_NUM_TEAMS;
	}
	let numAttempts = Math.floor(float);
	const remainder = float % 1;
	if (remainder > 0 && Math.random() < remainder) {
		numAttempts += 1;
	}

	if (numAttempts > 0) {
		const valueChangeCalculator = new ValueChangeCalculator();

		for (let i = 0; i < numAttempts; i++) {
			const tradeTids = await attempt(valueChangeCalculator);
			if (tradeTids) {
				// Don't need to recompute draft pick value
				valueChangeCalculator.invalidateCache({ teams: tradeTids });
			}
		}
	}
};

export default betweenAiTeams;
